/**
 * ProjectVariablesStep — create-wizard step 4 (W12 Part B, Task B3).
 *
 * Everything the funnel may ask the user: the project's global questions plus
 * the ones scoped to the selected goals. The «follow tasks» switch (AC-016…018,
 * default ON) is a ROW above the table — while it is on, task-scoped rows of
 * UNSELECTED routes are hidden, because they are not sent either.
 */
import React from 'react';
import { Box, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { SelectionTable, type SelectionRow } from '../SelectionTable';

export interface ProjectVariablesStepProps {
  rows: SelectionRow[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  followTasks: boolean;
  onFollowTasksChange: (next: boolean) => void;
  /** «N selected · G global + T task-scoped», computed by the dialog. */
  summary: string;
  /** «Product variables: N/M» chip. */
  productCountLabel: string;
  /** «Insight-derived: K/A» chip. */
  insightCountLabel: string;
  hiddenByFollowCount: number;
  disabled?: boolean;
}

export const ProjectVariablesStep: React.FC<ProjectVariablesStepProps> = ({
  rows,
  selectedIds,
  onSelectedIdsChange,
  followTasks,
  onFollowTasksChange,
  summary,
  productCountLabel,
  insightCountLabel,
  hiddenByFollowCount,
  disabled = false,
}) => (
  <Stack spacing={2}>
    <FormControlLabel
      sx={{ mr: 0, alignSelf: 'flex-start' }}
      control={(
        <Switch
          size="small"
          checked={followTasks}
          disabled={disabled}
          onChange={(event) => onFollowTasksChange(event.target.checked)}
          inputProps={{ 'aria-label': 'Task-scoped variables follow selected tasks' }}
          data-testid={STRATEGY_LIST.variablesFollowTasksSwitch}
        />
      )}
      label={(
        <Typography variant="body2" color="text.secondary">
          Task-scoped variables follow selected tasks
          {followTasks && hiddenByFollowCount > 0
            ? ` · ${hiddenByFollowCount} hidden by unselected goals`
            : ''}
        </Typography>
      )}
    />

    <SelectionTable
      rows={rows}
      selectedIds={selectedIds}
      onSelectedIdsChange={onSelectedIdsChange}
      searchPlaceholder="Search variables"
      summary={summary}
      emptyState={(
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No project variables yet. You can add them after creation, in strategy edit mode.
        </Typography>
      )}
      aria-label="Project variables"
      data-testid={STRATEGY_LIST.wizardTable('projectVars')}
    />

    <Box
      data-testid={STRATEGY_LIST.variablePackageSummary}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}
    >
      <Typography variant="subtitle2">Variable package</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          variant="outlined"
          label={productCountLabel}
          data-testid={STRATEGY_LIST.variablePackageProductCount}
        />
        <Chip
          size="small"
          variant="outlined"
          label={insightCountLabel}
          data-testid={STRATEGY_LIST.variablePackageInsightCount}
        />
      </Stack>
    </Box>
  </Stack>
);

ProjectVariablesStep.displayName = 'ProjectVariablesStep';
